import { useEffect, useRef, useState } from 'react';

export type PinGateMode = 'verify' | 'create' | 'off';

const INIT_DELAY = 700;
const VERIFY_DELAY = 600;

function sanitizePin(value: string): string {
  return value.replace(/\D/g, '').slice(0, 4);
}

/** Simulates the Salesforce PIN gate that protects the commission dashboard. */
export function usePinGate(mode: PinGateMode) {
  const [pinGateInitializing, setInitializing] = useState(mode !== 'off');
  const [pinGateOpen, setOpen] = useState(false);
  const [unlocked, setUnlocked] = useState(mode === 'off');
  const [pinValue, setPinValue] = useState('');
  const [pinConfirmValue, setPinConfirmValue] = useState('');
  const [pinError, setPinError] = useState<string | null>(null);
  const [isVerifyingPin, setIsVerifyingPin] = useState(false);
  const verifyTimer = useRef<number>();

  useEffect(() => {
    setPinValue('');
    setPinConfirmValue('');
    setPinError(null);
    setIsVerifyingPin(false);
    if (mode === 'off') {
      setInitializing(false);
      setOpen(false);
      setUnlocked(true);
      return undefined;
    }
    setUnlocked(false);
    setOpen(false);
    setInitializing(true);
    const timer = window.setTimeout(() => {
      setInitializing(false);
      setOpen(true);
    }, INIT_DELAY);
    return () => window.clearTimeout(timer);
  }, [mode]);

  useEffect(() => () => window.clearTimeout(verifyTimer.current), []);

  const isPinCreateMode = mode === 'create';

  function handlePinInput(value: string) {
    setPinValue(sanitizePin(value));
    setPinError(null);
  }

  function handlePinConfirmInput(value: string) {
    setPinConfirmValue(sanitizePin(value));
    setPinError(null);
  }

  function submitCommissionPin() {
    if (pinValue.length !== 4) {
      setPinError('Enter all 4 digits of your PIN.');
      return;
    }
    if (isPinCreateMode && pinValue !== pinConfirmValue) {
      setPinError('PINs don’t match. Re-enter your confirmation PIN.');
      return;
    }
    setIsVerifyingPin(true);
    verifyTimer.current = window.setTimeout(() => {
      setIsVerifyingPin(false);
      if (!isPinCreateMode && pinValue === '0000') {
        setPinError('Incorrect PIN. Please try again.');
        setPinValue('');
        return;
      }
      setOpen(false);
      setUnlocked(true);
    }, VERIFY_DELAY);
  }

  function closePinModal() {
    setOpen(false);
    setPinValue('');
    setPinConfirmValue('');
    setPinError(null);
  }

  function reopenPinModal() {
    setOpen(true);
  }

  return {
    pinGateInitializing,
    pinGateOpen,
    unlocked,
    isLockedOut: !pinGateInitializing && !pinGateOpen && !unlocked,
    isPinCreateMode,
    pinValue,
    pinConfirmValue,
    pinError,
    isVerifyingPin,
    pinModalTitle: isPinCreateMode ? 'Create your dashboard PIN' : 'Enter your dashboard PIN',
    pinModalSubtitle: isPinCreateMode
      ? 'Set a 4-digit PIN to protect your commission details. You’ll use it each time you open this dashboard.'
      : 'Your commission details are protected. Enter your 4-digit PIN to continue.',
    pinSubmitLabel: isVerifyingPin
      ? isPinCreateMode
        ? 'Saving…'
        : 'Verifying…'
      : isPinCreateMode
        ? 'Create PIN'
        : 'Unlock',
    pinSubmitDisabled: isVerifyingPin || pinValue.length < 4 || (isPinCreateMode && pinConfirmValue.length < 4),
    handlePinInput,
    handlePinConfirmInput,
    submitCommissionPin,
    closePinModal,
    reopenPinModal,
  };
}

export type PinGateState = ReturnType<typeof usePinGate>;
